import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type {
  Guest,
  GuestInsert,
  GuestUpdate,
  GuestWithDetails,
} from '@/lib/types'

export interface UseGuestsResult {
  guests: Guest[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  addGuest: (guest: GuestInsert) => Promise<Guest | null>
  updateGuest: (id: string, updates: GuestUpdate) => Promise<boolean>
  deleteGuest: (id: string) => Promise<boolean>
  getGuestById: (id: string) => Promise<GuestWithDetails | null>
  getGuestByPin: (pin: string) => Promise<GuestWithDetails | null>
}

export function useGuests(): UseGuestsResult {
  const [guests, setGuests] = useState<Guest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchGuests = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch guests with additional_guests count
      const { data, error: fetchError } = await supabase
        .from('guests')
        .select(`
          *,
          additional_guests(*)
        `)
        .order('created_date', { ascending: false })

      if (fetchError) throw fetchError

      setGuests(data || [])
    } catch (err) {
      console.error('Error fetching guests:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch guests')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchGuests()

    // Set up real-time subscription for guests table
    const channel = supabase
      .channel('guests-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'guests',
        },
        (payload) => {
          console.log('Guest change detected:', payload)
          fetchGuests() // Refetch all guests on any change
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [fetchGuests])

  const addGuest = useCallback(async (guestData: GuestInsert): Promise<Guest | null> => {
    try {
      setError(null)

      const { data, error: insertError } = await supabase
        .from('guests')
        .insert(guestData)
        .select()
        .single()

      if (insertError) throw insertError

      // Immediately refetch to update the UI
      await fetchGuests()

      return data
    } catch (err) {
      console.error('Error adding guest:', err)
      setError(err instanceof Error ? err.message : 'Failed to add guest')
      return null
    }
  }, [fetchGuests])

  const updateGuest = useCallback(async (id: string, updates: GuestUpdate): Promise<boolean> => {
    try {
      setError(null)

      // Set session variable for changelog tracking (admin context)
      try {
        const { error: rpcError } = await supabase.rpc('exec_sql', {
          query: "SELECT set_config('app.changed_by', 'admin', false)",
        })
        if (rpcError) {
          console.warn('Failed to set session variable:', rpcError)
        }
      } catch (rpcErr) {
        console.warn('RPC call failed, continuing anyway:', rpcErr)
      }

      const { data, error: updateError } = await supabase
        .from('guests')
        .update(updates)
        .eq('id', id)
        .select()

      if (updateError) {
        console.error('Update error details:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code
        })
        throw updateError
      }

      console.log('Update successful:', data)

      // Immediately refetch to update the UI
      await fetchGuests()

      return true
    } catch (err) {
      console.error('Error updating guest:', err)
      setError(err instanceof Error ? err.message : 'Failed to update guest')
      return false
    }
  }, [fetchGuests])

  const deleteGuest = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null)

      const { error: deleteError } = await supabase
        .from('guests')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      await fetchGuests()

      return true
    } catch (err) {
      console.error('Error deleting guest:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete guest')
      return false
    }
  }, [fetchGuests])

  const getGuestById = useCallback(async (id: string): Promise<GuestWithDetails | null> => {
    try {
      setError(null)

      const { data: guestData, error: guestError } = await supabase
        .from('guests')
        .select('*')
        .eq('id', id)
        .single()

      if (guestError) throw guestError

      const { data: additionalGuestsData, error: additionalError } = await supabase
        .from('additional_guests')
        .select('*')
        .eq('guest_id', id)
        .order('added_date', { ascending: true })

      if (additionalError) throw additionalError

      const { data: changelogData, error: changelogError } = await supabase
        .from('changelog')
        .select('*')
        .eq('guest_id', id)
        .order('timestamp', { ascending: false })

      if (changelogError) throw changelogError

      return {
        ...guestData,
        additional_guests: additionalGuestsData || [],
        changelog: changelogData || [],
      }
    } catch (err) {
      console.error('Error fetching guest details:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch guest details')
      return null
    }
  }, [])

  const getGuestByPin = useCallback(async (pin: string): Promise<GuestWithDetails | null> => {
    try {
      setError(null)

      const { data: guestData, error: guestError } = await supabase
        .from('guests')
        .select('*')
        .eq('pin', pin)
        .single()

      if (guestError) throw guestError

      const { data: additionalGuestsData, error: additionalError } = await supabase
        .from('additional_guests')
        .select('*')
        .eq('guest_id', guestData.id)
        .order('added_date', { ascending: true })

      if (additionalError) throw additionalError

      const { data: changelogData, error: changelogError } = await supabase
        .from('changelog')
        .select('*')
        .eq('guest_id', guestData.id)
        .order('timestamp', { ascending: false })

      if (changelogError) throw changelogError

      return {
        ...guestData,
        additional_guests: additionalGuestsData || [],
        changelog: changelogData || [],
      }
    } catch (err) {
      console.error('Error fetching guest by PIN:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch guest by PIN')
      return null
    }
  }, [])

  return {
    guests,
    loading,
    error,
    refetch: fetchGuests,
    addGuest,
    updateGuest,
    deleteGuest,
    getGuestById,
    getGuestByPin,
  }
}

